import { DatePipe } from '@angular/common';
import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonIconComponent,
	CollapseComponent,
	IconComponent,
	InfoGroupComponent,
	ITab,
	ITagStatus,
	ITagWithLabel,
	ItemComponent,
	LinkComponent,
	ListDetailComponent,
	ModalComponent,
	TabsComponent,
	TagComponent,
	TagGroupComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import {
	CONNECTION_TYPE,
	DOCS_TYPE,
	IDocumentsDocs,
	IDocumentsOperation,
	IOperation,
	OPERATION_TYPE,
} from '../../../../operations/models/operations.models';
import { IWorkflowDetailResponse } from '../../../models/workflows.models';
import { WorkflowsHelperService } from '../../../services/workflows-helper.service';
import { DocsOperationComponent } from './docs-operation/docs-operation.component';
import { FinancialEvaluationOperationComponent } from './financial-evaluation-operation/financial-evaluation-operation.component';
import { FinishOperationComponent } from './finish-operation/finish-operation.component';
import { HomeOperationComponent } from './home-operation/home-operation.component';
import { OcrOperationComponent } from './ocr-operation/ocr-operation.component';
import { PolOperationComponent } from './pol-operation/pol-operation.component';
import { UuidDisplayComponent } from '../../../../shared/components/uuid-display/uuid-display.component';
import { AttorneyPowersOperationComponent } from './attorney-powers-operation/attorney-powers-operation/attorney-powers-operation.component';
import { ChooseOperationComponent } from './choose-operation/choose-operation.component';
import { FormOperationComponent } from './form-operation/form-operation.component';
import { TextLocalePreviewComponent } from '../../../../shared/components/text-locale-preview/text-locale-preview.component';
import { Permission } from '../../../../core/models/role-permissions.models';
import { RolePermissionsService } from '../../../../core/services/role-permissions.service';
import { environment } from '../../../../../environments/environment';
import { buildCurlGet } from '../../../../panel/utils/build-curl.util';
import { ModalCurlRequestComponent } from '../../../../shared/components/modal-curl-request/modal-curl-request.component';

@Component({
	selector: 'app-workflows-detail-view',
	imports: [
		TranslatePipe,
		DatePipe,
		TabsComponent,
		CollapseComponent,
		TagComponent,
		IconComponent,
		TagGroupComponent,
		LinkComponent,
		ItemComponent,
		ListDetailComponent,
		TileComponent,
		InfoGroupComponent,
		HomeOperationComponent,
		OcrOperationComponent,
		PolOperationComponent,
		DocsOperationComponent,
		FinancialEvaluationOperationComponent,
		FinishOperationComponent,
		ModalComponent,
		UuidDisplayComponent,
		AttorneyPowersOperationComponent,
		ChooseOperationComponent,
		FormOperationComponent,
		TextLocalePreviewComponent,
		ButtonIconComponent,
		ModalCurlRequestComponent,
	],
	templateUrl: './workflows-detail-view.component.html',
	styleUrl: './workflows-detail-view.component.scss',
})
export class WorkflowsDetailViewComponent implements OnInit {
	@Input() detail: IWorkflowDetailResponse;

	public workflowsHelper = inject(WorkflowsHelperService);
	private trans = inject(TranslateService);
	public rolePermissions = inject(RolePermissionsService);

	public OPERATION_TYPE: typeof OPERATION_TYPE = OPERATION_TYPE;
	public CONNECTION_TYPE: typeof CONNECTION_TYPE = CONNECTION_TYPE;

	public readonly curlModalOpen = signal(false);
	public readonly curlModalTitleKey = signal('WORKFLOWS.CURL_MODAL.TITLE');
	public readonly curlModalText = signal('');
	public tabs: ITab[] = this.workflowsHelper.detailTabs;
	public allowedDeviceTags: ITagWithLabel[] = [];
	public showDelayedProcessTextModal: boolean = false;

	ngOnInit(): void {
		this.allowedDeviceTags = this.getAllowedDeviceTags();
	}

	public canViewDeveloperCurl(): boolean {
		return this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION);
	}

	public openCurlModal(titleKey: string, path: string): void {
		this.curlModalTitleKey.set(titleKey);
		this.curlModalText.set(buildCurlGet(environment.apiUrl, path));
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public sectionCurlPath(section: string): string {
		const uuid = this.detail?.uuid || '';
		return `/profile/${uuid}/sections/${section}`;
	}

	public getAllowedDeviceTags(): ITagWithLabel[] {
		const devices = this.detail?.config?.allowed_device || [];
		return devices.map((device) => {
			return { label: this.trans.instant(`MASTER.ALLOWED_DEVICES.${device?.toUpperCase()}`) };
		});
	}

	public getStatus(value: boolean = false): ITagStatus {
		return value ? 'success' : 'disabled';
	}

	public getStatusLabel(value: boolean = false): string {
		return this.trans.instant('WORKFLOWS.DETAIL.' + (value ? 'ACTIVE' : 'INACTIVE'));
	}

	public getWorkflowTwoFactorChannel(): 'none' | 'whatsapp' | 'sms' {
		const channelFromTfa = this.detail?.config?.tfa?.type;
		if (channelFromTfa === 'sms' || channelFromTfa === 'whatsapp') {
			return channelFromTfa;
		}
		const metadata = this.detail?.config?.metadata as Record<string, unknown> | undefined;
		const channelFromMetadata = metadata?.['two_factor_channel'];
		if (channelFromMetadata === 'sms' || channelFromMetadata === 'whatsapp') {
			return channelFromMetadata;
		}
		return 'none';
	}

	public getWorkflowTwoFactorLabel(): string {
		const channel = this.getWorkflowTwoFactorChannel();
		if (channel === 'sms') {
			return this.trans.instant('WORKFLOWS.FORM.GENERAL.TWO_FACTOR_SMS');
		}
		if (channel === 'whatsapp') {
			return this.trans.instant('WORKFLOWS.FORM.GENERAL.TWO_FACTOR_WHATSAPP');
		}
		return this.trans.instant('WORKFLOWS.FORM.GENERAL.TWO_FACTOR_NONE');
	}

	public hasWorkflowTwoFactorEnabled(): boolean {
		return this.getWorkflowTwoFactorChannel() !== 'none';
	}

	public hasConnecLikeDocAndNotDocsOperation(): boolean {
		const hasConnectionLikeDoc = Boolean(
			this.detail.operations.find(
				(op: IOperation) => op.type === OPERATION_TYPE.CONNECTIONS && !op.config?.evaluation,
			),
		);
		const hasDocument = Boolean(
			this.detail.operations.find((op: IOperation) => op.type === OPERATION_TYPE.DOCUMENTS),
		);
		return hasConnectionLikeDoc && !hasDocument;
	}

	public hasAttorneyPowersLikeDocs(): boolean {
		return Boolean(
			this.detail.operations.find(
				(op: IOperation) =>
					op.type === OPERATION_TYPE.DOCUMENTS &&
					op.config.docs.find((doc: IDocumentsDocs) => doc.type === DOCS_TYPE.SCRIPTURE),
			) as IDocumentsOperation,
		);
	}

	public hasConnecLikeDocAndDocsOperation(): boolean {
		const hasConnectionLikeDoc = Boolean(
			this.detail.operations.find(
				(op: IOperation) => op.type === OPERATION_TYPE.CONNECTIONS && !op.config?.evaluation,
			),
		);
		const hasDocument = Boolean(
			this.detail.operations.find((op: IOperation) => op.type === OPERATION_TYPE.DOCUMENTS),
		);
		return hasConnectionLikeDoc && hasDocument;
	}

	public getValidationTypeOpt(detail?: IWorkflowDetailResponse): 'AUTO' | 'MANUAL' {
		const validationType = detail?.config?.validation?.type as unknown;
		if (!validationType) return 'AUTO';

		// Contrato: `{ type: 'auto'|'ok'|'ko'|'ok-ko', errors }` (a veces aplana a string)
		const processType =
			typeof validationType === 'string'
				? validationType
				: (validationType as { type?: unknown } | null | undefined)?.type;

		return processType === 'ok' || processType === 'ko' || processType === 'ok-ko' || processType === 'ok_ko'
			? 'MANUAL'
			: 'AUTO';
	}
}
